import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  CheckSquare, Clock, AlertCircle, Calendar, RefreshCw,
  Users, DollarSign, FileText, ChevronRight
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, { withCredentials: true });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const completeTask = async (taskId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/complete`, {}, { withCredentials: true });
      setTasks(tasks.map(t => t.task_id === taskId ? {...t, status: 'completed'} : t));
      toast.success("Task completed!");
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  const getRelatedIcon = (relatedTo) => {
    switch (relatedTo) {
      case 'lead': return <Users className="w-4 h-4" />;
      case 'deal': return <DollarSign className="w-4 h-4" />;
      case 'contract': return <FileText className="w-4 h-4" />;
      case 'customer': return <Users className="w-4 h-4" />;
      default: return <CheckSquare className="w-4 h-4" />;
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === "pending") return t.status === "pending";
    if (filter === "completed") return t.status === "completed";
    if (filter === "overdue") return t.status === "pending" && isOverdue(t.due_date);
    return true;
  });

  const pendingCount = tasks.filter(t => t.status === "pending").length;
  const overdueCount = tasks.filter(t => t.status === "pending" && isOverdue(t.due_date)).length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tasks-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground">Tasks</h1>
            <p className="text-muted-foreground mt-1">
              {pendingCount} pending tasks {overdueCount > 0 && `(${overdueCount} overdue)`}
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={fetchTasks}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card cursor-pointer" onClick={() => setFilter("all")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CheckSquare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{tasks.length}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card cursor-pointer" onClick={() => setFilter("pending")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{pendingCount}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card cursor-pointer" onClick={() => setFilter("overdue")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{overdueCount}</p>
                  <p className="text-xs text-muted-foreground">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card cursor-pointer" onClick={() => setFilter("completed")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckSquare className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{tasks.filter(t => t.status === 'completed').length}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          {["all", "pending", "overdue", "completed"].map(f => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              className="rounded-full capitalize"
            >
              {f}
            </Button>
          ))}
        </div>

        {/* Tasks List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="font-heading">
              {filter === "all" ? "All Tasks" : 
               filter === "pending" ? "Pending Tasks" :
               filter === "overdue" ? "Overdue Tasks" : "Completed Tasks"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-16 bg-muted/50 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : filteredTasks.length > 0 ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3 pr-4">
                  {filteredTasks.map((task) => (
                    <div 
                      key={task.task_id}
                      className={`flex items-start gap-4 p-4 rounded-xl border transition-colors ${
                        task.status === 'completed' 
                          ? 'bg-muted/20 border-border/50' 
                          : isOverdue(task.due_date)
                          ? 'bg-red-500/5 border-red-500/20'
                          : 'bg-muted/30 border-border/50 hover:bg-muted/50'
                      }`}
                      data-testid={`task-${task.task_id}`}
                    >
                      <Checkbox
                        checked={task.status === 'completed'}
                        onCheckedChange={() => task.status !== 'completed' && completeTask(task.task_id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${
                          task.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground'
                        }`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {task.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          {task.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${
                              isOverdue(task.due_date) && task.status !== 'completed' 
                                ? 'text-red-500' 
                                : 'text-muted-foreground'
                            }`}>
                              <Calendar className="w-3 h-3" />
                              {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          )}
                          {task.related_to && (
                            <Badge variant="outline" className="text-xs">
                              {getRelatedIcon(task.related_to)}
                              <span className="ml-1 capitalize">{task.related_to}</span>
                            </Badge>
                          )}
                        </div>
                      </div>
                      {task.status !== 'completed' && isOverdue(task.due_date) && (
                        <Badge className="bg-red-500/10 text-red-500">Overdue</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12">
                <CheckSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No tasks found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Tasks created by workflows will appear here
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
